import { type FC, useEffect, useState } from "react";
import { Select, type SelectOptionType } from "@onshopfront/components";
import { type Override } from "../../../../types/internal/utility";
import { useShopfrontDispatch } from "../../../_Bootstrap/hooks";
import { hideDialog } from "../../../Actions/Dialogs/DialogActions";
import Button from "../../../Common/Buttons/Button";
import NiceError from "../../../Common/Errors/NiceError";
import PaddingInput, { type PaddingType } from "../../../Common/Forms/PaddingInput";
import LoadingScreen from "../../../Common/Loading/LoadingScreen";
import PopupManager from "../../../Common/Popups/PopupManager";
import { type ReceiptPaddingType, type ReceiptServerTypeUnion } from "../../../Data/_Bases/BaseReceiptRepository";
import { determineDataSource } from "../../../Data/DataStores/DataSource";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import Request from "../../../Utilities/Platform/Request";

interface ReceiptConfigureReceiptType {
    type: ReceiptServerTypeUnion;
    padding: null | ReceiptPaddingType;
    attachments: Array<{
        name: string;
        attachment: {
            id: string;
            name: string;
        };
    }>;
    receiptWidth: string | null;
}

function formatPaddingValue(padding: PaddingType): null | ReceiptPaddingType {
    const top = padding.top === null ? NaN : parseFloat(padding.top.toString());
    const left = padding.left === null ? NaN : parseFloat(padding.left.toString());
    const right = padding.right === null ? NaN : parseFloat(padding.right.toString());
    const bottom = padding.bottom === null ? NaN : parseFloat(padding.bottom.toString());

    const topNaN = isNaN(top);
    const leftNaN = isNaN(left);
    const rightNaN = isNaN(right);
    const bottomNaN = isNaN(bottom);

    if(topNaN && leftNaN && rightNaN && bottomNaN) {
        return null;
    }

    return {
        top   : topNaN ? null : top,
        left  : leftNaN ? null : left,
        right : rightNaN ? null : right,
        bottom: bottomNaN ? null : bottom,
    };
}

export const ReceiptConfigure: FC<{
    id: string;
    onConfirm?: (receipt: Override<ReceiptConfigureReceiptType, { receiptWidth: number | null; }>) => void;
}> = ({ id, onConfirm }) => {
    const [ receipt, setReceipt ] = useState<null | ReceiptConfigureReceiptType>(null);
    const [ receipts, setReceipts ] = useState<Array<{ id: string; name: string; }>>([]);
    const [ loading, setLoading ] = useState(true);
    const [ error, setError ] = useState(false);
    const [ saving, setSaving ] = useState(false);
    const [ padding, setPadding ] = useState<PaddingType>({
        top   : "",
        left  : "",
        right : "",
        bottom: "",
    });

    const dispatch = useShopfrontDispatch();

    useEffect(() => {
        setLoading(true);

        Request.post<{
            data: {
                receipt: ReceiptConfigureReceiptType;
                receipts: Array<{
                    id: string;
                    name: string;
                    type: ReceiptServerTypeUnion;
                }>;
            };
        }>("/graphql", {
            query: `
                query GetReceipt($id: ID!) {
                    receipt(id: $id) {
                        type,
                        receiptWidth,
                        attachments {
                            name,
                            attachment {
                                id,
                                name,
                            }
                        },
                        padding {
                            top,
                            left,
                            right,
                            bottom
                        }
                    },
                    receipts {
                        id,
                        name,
                        type,
                    }
                }
            `,
            variables: {
                id,
            },
        })
            .then(response => {
                const receipt = response.data.data.receipt;

                setReceipt(receipt);
                setReceipts(response.data.data.receipts.filter(r => r.type === "A4"));

                if(receipt.padding) {
                    setPadding(receipt.padding);
                }
            })
            .catch(err => {
                console.error("The receipt could not be loaded for configuration", err);
                setError(true);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [ dispatch, id ]);

    if(error) {
        return (
            <NiceError
                icon="plug"
                title="Unable to load receipt"
            >
                <p>We were unable to load the receipt details, please try again later.</p>
            </NiceError>
        );
    }

    if(loading || !receipt || saving) {
        return (
            <div className="relative w-40 h-40">
                <LoadingScreen />
            </div>
        );
    }

    return (
        <>
            <div className="max-h-[65vh] overflow-y-auto">
                {receipt.type !== "TEXT_ONLY" && (
                    <>
                        <p>Receipt Padding (in pixels)</p>
                        <PaddingInput
                            name="padding"
                            padding={padding}
                            onChange={(name, padding) => {
                                setPadding(padding);
                            }}
                        />
                    </>
                )}
                {receipt.type === "TEXT_ONLY" && (
                    <label>
                        Receipt Width
                        <input
                            type="number"
                            name="receiptWidth"
                            value={receipt.receiptWidth ?? ""}
                            onChange={e => {
                                const width = e.currentTarget.value;

                                setReceipt(prev => prev === null ? null : ({
                                    ...prev,
                                    receiptWidth: width,
                                }));
                            }}
                        />
                    </label>
                )}
                {receipt.type === "EMAIL" && (
                    <div>
                        <p>Receipt Attachments</p>
                        <table className="no-border">
                            {receipt.attachments.length !== 0 && (
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Receipt</th>
                                        <th />
                                    </tr>
                                </thead>
                            )}
                            <tbody>
                                {receipt.attachments.map((attachment, index) => (
                                    <tr key={index}>
                                        <td>
                                            <input
                                                type="text"
                                                value={attachment.name}
                                                onChange={e => {
                                                    const name = e.currentTarget.value;

                                                    setReceipt(prev => prev === null ? null : ({
                                                        ...prev,
                                                        attachments: [
                                                            ...prev.attachments.slice(0, index),
                                                            {
                                                                ...prev.attachments[index],
                                                                name,
                                                            },
                                                            ...prev.attachments.slice(index + 1),
                                                        ],
                                                    }));
                                                }}
                                            />
                                        </td>
                                        <td>
                                            <Select
                                                value={{
                                                    label: attachment.attachment.name,
                                                    value: attachment.attachment.id,
                                                }}
                                                onChange={(value: SelectOptionType<string>) => {
                                                    setReceipt(prev => prev === null ? null : ({
                                                        ...prev,
                                                        attachments: [
                                                            ...prev.attachments.slice(0, index),
                                                            {
                                                                ...prev.attachments[index],
                                                                attachment: {
                                                                    name: value.label as string,
                                                                    id  : value.value,
                                                                },
                                                            },
                                                            ...prev.attachments.slice(index + 1),
                                                        ],
                                                    }));
                                                }}
                                                options={receipts.map(r => ({
                                                    label: r.name,
                                                    value: r.id,
                                                }))}
                                            />
                                        </td>
                                        <td
                                            className="cursor-pointer text-red-500 hover:text-red-700"
                                            onClick={() => {
                                                setReceipt(prev => prev === null ? null : ({
                                                    ...prev,
                                                    attachments: [
                                                        ...prev.attachments.slice(0, index),
                                                        ...prev.attachments.slice(index + 1),
                                                    ],
                                                }));
                                            }}
                                        >
                                            <FontAwesomeLight icon="trash" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={3}>
                                        <Button

                                            onClick={() => {
                                                setReceipt(prev => prev === null ? null : ({
                                                    ...prev,
                                                    attachments: [
                                                        ...prev.attachments,
                                                        {
                                                            name      : "{outlet.name} Invoice #{invoiceNo}",
                                                            attachment: {
                                                                id  : "",
                                                                name: "",
                                                            },
                                                        },
                                                    ],
                                                }));
                                            }}
                                        >
                                            Add Attachment
                                        </Button>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </div>
            <div className="buttons">
                <Button
                    type="button"
                    onClick={() => dispatch(hideDialog())}
                    classes={[ "button-cancel" ]}
                >
                    Cancel
                </Button>
                <Button
                    type="button"
                    onClick={() => {
                        if(!receipt) {
                            PopupManager.info("Please wait while the receipt loads");

                            return;
                        }

                        const width = receipt.receiptWidth === null ? null : parseFloat(receipt.receiptWidth);

                        if(width !== null && isNaN(width)) {
                            PopupManager.error("Please specify a valid receipt width");

                            return;
                        }

                        for(let i = 0, l = receipt.attachments.length; i < l; i++) {
                            if(!receipt.attachments[i].name.trim()) {
                                PopupManager.error("Please specify a valid attachment name");

                                return;
                            }

                            if(!receipt.attachments[i].attachment.id) {
                                PopupManager.error("Please select which receipt you wish to attach");

                                return;
                            }
                        }

                        const formattedPadding = formatPaddingValue(padding);

                        setSaving(true);
                        Request.post<{
                            errors?: Array<unknown>;
                        }>("/graphql", {
                            query: `
                                mutation UpdateReceipt(
                                    $id: ID!, 
                                    $padding: ReceiptPaddingInput,
                                    $receiptWidth: Float, 
                                    $attachments: [ReceiptAttachmentInput!]
                                ) {
                                    updateReceipt(
                                        id: $id,
                                        padding: $padding,
                                        receiptWidth: $receiptWidth,
                                        attachments: $attachments
                                    ) {
                                        id,
                                    }
                                }
                            `,
                            variables: {
                                id,
                                padding     : formattedPadding,
                                receiptWidth: width,
                                attachments : receipt.type !== "EMAIL" ? null : receipt.attachments.map(attachment => ({
                                    id  : attachment.attachment.id,
                                    name: attachment.name,
                                })),
                            },
                        })
                            .then(response => {
                                if(response.data.errors) {
                                    throw response.data.errors;
                                }

                                onConfirm?.({
                                    ...receipt,
                                    padding     : formattedPadding,
                                    receiptWidth: width,
                                });

                                determineDataSource()
                                    .then(source => {
                                        return source.receipts.update(id, {
                                            padding     : formattedPadding,
                                            receiptWidth: width ?? 40,
                                            attachments : receipt.type !== "EMAIL" ? (
                                                []
                                            ) : (
                                                receipt.attachments.map(attachment => ({
                                                    receipt: attachment.attachment.id,
                                                    name   : attachment.name,
                                                }))
                                            ),
                                        });
                                    });

                                dispatch(hideDialog());
                                PopupManager.success("Receipt configuration saved");
                            })
                            .catch(err => {
                                console.error("Unable to save receipt configuration", err);
                                setSaving(false);
                                PopupManager.error("We were unable to save the receipt configuration");
                            });
                    }}
                    classes={[ "button-save" ]}
                >
                    Confirm
                </Button>
            </div>
        </>
    );
};
